<template>
  <TasksAccordion
    v-if="showTasks"
    :value="status"
    :disabled="totalTasks === 0"
    :class="{
      'rounded-lg': !maxSmall,
    }"
    class="border border-secondary"
    :status="status"
    :count="totalTasks"
    :countRemaining="totalTasks - tasks?.length"
    :loading="isInitiallyLoading"
    :showLoadMore="!isLastPage && !isInitiallyLoading"
    @load-more="getTasks()"
  >
    <template #default>
      <Task
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        type="list"
        :checked="selectedRows[task.id]"
        :isSelectable="isSelectable"
        @click="$emit('click:task', task)"
        @select="handleUpdateSelection([], task.id)"
        :class="{
          'border-b-0':
            (isLastPage && last(tasks) === task) || isInitiallyLoading,
        }"
      />
    </template>
  </TasksAccordion>
</template>

<script setup>
// Vue utilities
import { computed, onMounted, inject, ref, onUnmounted, watch } from "vue";

// Importing utility libraries
import { storeToRefs } from "pinia";
import route from "ziggy-js";
import axios from "axios";
import { uniqBy, isEmpty, last } from "lodash";

// Import Components
import { Task } from "@/SharedComponents/Tasks";
import { TasksAccordion } from "@/SharedComponents/Tasks/TasksAccordion";

// Import Components
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useSorting } from "@/Composables/useSorting";
import { useFilters } from "@/Composables/useFilters";
import { useSearch } from "@/Composables/useSearch";
import { useDefaultTasksViews } from "@/Pages/Tasks/Views";
import { useTaskEvents } from "@/Composables/useTaskEvents";
import { useSelectedRows } from "@/Composables/useSelectedRows";

// Emits
const emit = defineEmits(["click:task", "update:loading", "update:selected"]);

// Emitter
const emitter = inject("emitter");

// Breakpoints
const { maxSmall } = useBreakpoints();

// Sorting
const sortingStore = useSorting("tasks");
const { sortingValues } = storeToRefs(sortingStore);

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Filters: Tasks
const filterStore = useFilters("tasks");
const { filterValues, activeView } = storeToRefs(filterStore);

// Search
const searchStore = useSearch("tasks");
const { searchValues } = storeToRefs(searchStore);

// Default views
const { DefaultView } = useDefaultTasksViews();

// Selected Rows
const selectedRowsStore = useSelectedRows("tasks");
const { selectedRows, selection } = storeToRefs(selectedRowsStore);
const { handleUpdateSelection, handleClearSelection } = selectedRowsStore;

// Define Props
const props = defineProps({
  status: { type: String, required: true },
  isSelectable: { type: Boolean, required: false },
});

// Refs
const totalTasks = ref(0);
const tasks = ref([]);
const page = ref(1);
const isLastPage = ref(false);
const isLoading = ref(false);
const isInitiallyLoading = ref(true);
const per_page = ref(10);

// Computed Props
const selectedTasks = computed(() =>
  tasks.value.filter((task) => selection.value.includes(task.id))
);

watch(selectedTasks, () => {
  emit("update:selected", selectedTasks.value);
});

const filters = computed(() => {
  return {
    ...sortingValues.value,
    ...searchValues.value,
    ...filterValues.value,
    tenant: globalFilterValues.value.tenant,
    tenant_only: filterValues.value.tenant?.length > 0,
    archived_only: activeView.value.id === DefaultView.ARCHIVED.id,
  };
});

watch(filters, () => {
  tasks.value = [];
  isLastPage.value = false;
  page.value = 1;
  handleClearSelection();
  getTasks();
});

const filtersWithStatus = computed(() => {
  return {
    ...filters.value,
    status: [props.status],
  };
});

useTaskEvents({
  tasks,
  filters: filtersWithStatus,
  isLastPage,
  callback: (change) => {
    if (isLoading.value) {
      return;
    }

    totalTasks.value = totalTasks.value + change;
    emit("update:loading", {
      status: props.status,
      fetchingStatus: "loaded",
      count: totalTasks.value,
    });
  },
});

const includeStatus = computed(() => {
  const { status } = filters.value;
  return !status || isEmpty(status) || status.includes(props.status);
});

const showTasks = computed(() => {
  return includeStatus.value;
});

// Functions
const subscriptionCollapsible = () => {
  tasks.value = [];
  isLastPage.value = false;
  page.value = 1;

  getTasks();
};

async function getTasks() {
  if (isLastPage.value === true || isLoading.value === true) {
    return;
  }

  // Update the isLoading
  isLoading.value = true;

  // Emit update:loading
  emit("update:loading", {
    status: props.status,
    fetchingStatus: "loading",
    count: totalTasks.value,
  });

  const parameters = {
    page: page.value,
    ...filters.value,
    per_page: per_page.value,
    status: [props.status],
  };

  const url = route("api.tasks.search");

  await axios
    .post(url, parameters)
    .then((response) => {
      // Append the newly retrieved tasks to the list of already loaded tasks.
      tasks.value = uniqBy([...tasks.value, ...response.data.data], "id");

      // Assign the total tasks to totalTasks.
      totalTasks.value = response.data.meta.total;

      // Increment the page number to load the next batch of tasks.
      page.value = page.value + 1;

      // There are no more subsequent pages to load.
      if (response.data.links.next === null) {
        isLastPage.value = true;
      }

      // Emit update:loading
      emit("update:loading", {
        status: props.status,
        fetchingStatus: "loaded",
        count: totalTasks.value,
      });
    })
    .catch((err) => {
      console.log("error occurred", err);

      // Assign the total tasks to totalTasks.
      totalTasks.value = 0;

      // Emit update:loading
      emit("update:loading", {
        status: props.status,
        fetchingStatus: "error",
        count: totalTasks.value,
      });
    })
    .finally(() => {
      // Update the isInitiallyLoading
      isInitiallyLoading.value = false;

      // Update the isLoading
      isLoading.value = false;
    });
}

// Lifecycle Hooks
onMounted(() => {
  getTasks();
});

onUnmounted(() => {
  emitter.off("reload-tasks", subscriptionCollapsible);
});

// Emitters
emitter.on("reload-tasks", subscriptionCollapsible);
</script>
